// The bonus policy inputs, shared by the roster-wide editor and the per-therapist
// one so the two can never drift. The policy is three gates that must ALL pass,
// then three hours→percent tiers. Until now these lived only in salary_rates.json,
// which meant "change the minimum to 25 hours" was a code change (Ohad 2026-07-26).

export interface BonusValues {
  bonusEnabled: boolean;
  bonusNoshowThreshold: number;
  bonusUtilizationThreshold: number;
  bonusTier1MinHours: number; bonusTier1Pct: number;
  bonusTier2MinHours: number; bonusTier2Pct: number;
  bonusTier3MinHours: number; bonusTier3Pct: number;
}

export const BONUS_FIELDS: (keyof BonusValues)[] = [
  'bonusEnabled',
  'bonusNoshowThreshold', 'bonusUtilizationThreshold',
  'bonusTier1MinHours', 'bonusTier1Pct',
  'bonusTier2MinHours', 'bonusTier2Pct',
  'bonusTier3MinHours', 'bonusTier3Pct',
];

/** Every value the API sends arrives as a number; the form keeps them as strings. */
export type BonusDraft = Record<string, string>;

export const toDraft = (v: BonusValues): BonusDraft => ({
  bonusNoshowThreshold: String(v.bonusNoshowThreshold),
  bonusUtilizationThreshold: String(v.bonusUtilizationThreshold),
  bonusTier1MinHours: String(v.bonusTier1MinHours), bonusTier1Pct: String(v.bonusTier1Pct),
  bonusTier2MinHours: String(v.bonusTier2MinHours), bonusTier2Pct: String(v.bonusTier2Pct),
  bonusTier3MinHours: String(v.bonusTier3MinHours), bonusTier3Pct: String(v.bonusTier3Pct),
});

/** Draft + enabled flag → the {field: number} payload the override endpoint stores. */
export const toPayload = (d: BonusDraft, enabled: boolean): Record<string, number> => ({
  bonusEnabled: enabled ? 1 : 0,
  bonusNoshowThreshold: Number(d.bonusNoshowThreshold) || 0,
  bonusUtilizationThreshold: Number(d.bonusUtilizationThreshold) || 0,
  bonusTier1MinHours: Number(d.bonusTier1MinHours) || 0,
  bonusTier1Pct: Number(d.bonusTier1Pct) || 0,
  bonusTier2MinHours: Number(d.bonusTier2MinHours) || 0,
  bonusTier2Pct: Number(d.bonusTier2Pct) || 0,
  bonusTier3MinHours: Number(d.bonusTier3MinHours) || 0,
  bonusTier3Pct: Number(d.bonusTier3Pct) || 0,
});

const TIERS = [
  { hours: 'bonusTier1MinHours', pct: 'bonusTier1Pct', label: 'מדרגה 1' },
  { hours: 'bonusTier2MinHours', pct: 'bonusTier2Pct', label: 'מדרגה 2' },
  { hours: 'bonusTier3MinHours', pct: 'bonusTier3Pct', label: 'מדרגה 3' },
];

const num = 'w-20 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums';

interface Props {
  draft: BonusDraft;
  enabled: boolean;
  onChange: (d: BonusDraft) => void;
  onEnabledChange: (v: boolean) => void;
  disabled?: boolean;
}

export function BonusFields({ draft, enabled, onChange, onEnabledChange, disabled }: Props) {
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...draft, [k]: e.target.value });

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)} />
        בונוס פעיל
      </label>

      <div className="text-xs font-medium text-gray-500 mb-2">
        שערים — צריך לעבור את שלושתם
      </div>
      <div className="flex flex-wrap gap-4 mb-4">
        <label className="text-xs text-gray-500">
          no-show עד (%)
          <input type="number" step="0.5" value={draft.bonusNoshowThreshold}
            onChange={set('bonusNoshowThreshold')} className={`block mt-1 ${num}`} />
        </label>
        <label className="text-xs text-gray-500">
          ניצולת לפחות (%)
          <input type="number" step="0.5" value={draft.bonusUtilizationThreshold}
            onChange={set('bonusUtilizationThreshold')} className={`block mt-1 ${num}`} />
        </label>
        <div className="text-xs text-gray-500 self-end pb-1">
          שעות מינימום = מדרגה 1
        </div>
      </div>

      <div className="text-xs font-medium text-gray-500 mb-2">
        מדרגות — שעות ← אחוז מהתשלום הקליני
      </div>
      <div className="space-y-2">
        {TIERS.map((tier) => (
          <div key={tier.label} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">{tier.label}</span>
            <input type="number" step="0.5" value={draft[tier.hours]}
              onChange={set(tier.hours)} className={num} />
            <span className="text-xs text-gray-500">שעות ←</span>
            <input type="number" step="0.5" value={draft[tier.pct]}
              onChange={set(tier.pct)} className={num} />
            <span className="text-xs text-gray-500">%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
